/**
 * Retailer API Service — grocery ordering hand-off (spec: docs/superpowers/
 * specs/2026-08-14-grocery-ordering-integrations-design.md).
 *
 * With Better Auth, session cookies are sent automatically via credentials: 'include'.
 */

import type {
  RetailerLineKey,
  RetailerLineSelection,
  RetailerMatchResult,
  RetailerSummary,
} from "@batch-and-bowl/shared";
import { apiFetch } from "./client";

/**
 * Stable line identity as a Record key (#444, closes #416). MatchReview's
 * local selection state (and its hydration against the server's own
 * `selections`) is keyed by exactly this string, never by array index or
 * `GroceryItem.id` -- both of which can shift out from under a line across
 * a re-search or a grocery-list refresh.
 */
export function lineKeyOf(line: RetailerLineKey): string {
  return `${line.ingredientId}::${line.unit}`;
}

/**
 * List the retailers configured on this deployment, with capabilities and the
 * current user's link status. Retailers appear here only when the server has
 * their API keys — an empty list means the feature is effectively off.
 */
export async function getRetailers(): Promise<{ data: RetailerSummary[] }> {
  return apiFetch<{ data: RetailerSummary[] }>("/retailers");
}

export interface KrogerStore {
  locationId: string;
  name: string;
  chain?: string;
  address?: {
    addressLine1?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  };
}

/** Nearby Kroger stores for the store picker. No account link required. */
export async function getKrogerStores(
  zip: string,
): Promise<{ data: KrogerStore[] }> {
  return apiFetch<{ data: KrogerStore[] }>(
    `/retailers/kroger/stores?zip=${encodeURIComponent(zip)}`,
  );
}

export interface KrogerProductSearchResult {
  productId: string;
  upc?: string;
  description: string;
  brand?: string;
  size?: string;
  price?: number;
  aisle?: string;
  imageUrl?: string;
  suggestedQuantity?: number;
}

/** Live products and prices at the user's selected Kroger store. */
export async function searchKrogerProducts(
  term: string,
): Promise<{ data: KrogerProductSearchResult[] }> {
  return apiFetch<{ data: KrogerProductSearchResult[] }>(
    `/retailers/kroger/products?q=${encodeURIComponent(term)}`,
  );
}

/**
 * What the server persisted for the chosen store. The server resolves the
 * location's Kroger `chain` at selection time, so the reply names the banner
 * the shopper actually shops -- "King Soopers", not a blanket "Kroger".
 */
export interface KrogerStoreSelection {
  storeLocationId: string;
  storeChain: string | null;
  storeName: string | null;
  /** The banner's brand name; "Kroger" when the chain is unknown. */
  bannerName: string;
}

/** Persist the chosen store on the user's Kroger connection. */
export async function setKrogerStore(
  storeLocationId: string,
): Promise<KrogerStoreSelection> {
  return apiFetch<KrogerStoreSelection>("/retailers/kroger/store", {
    method: "PUT",
    body: JSON.stringify({ storeLocationId }),
  });
}

/** Remove the user's Kroger account link. */
export async function disconnectKroger(): Promise<void> {
  await apiFetch<void>("/retailers/kroger/connection", { method: "DELETE" });
}

/**
 * Match unchecked grocery-list items to products at the retailer (in-app
 * review data). Server-side this requires a linked account + selected store
 * for account-linked retailers.
 *
 * `forceLineKeys` (#444): force specific lines past the persisted match
 * cache even when it would otherwise call them fresh -- "Search again" on a
 * still-`unavailable` line uses this instead of clearing the whole cache.
 */
export async function matchRetailerItems(
  retailerId: string,
  groceryListId: string,
  forceLineKeys?: RetailerLineKey[],
): Promise<RetailerMatchResult> {
  return apiFetch(`/retailers/${retailerId}/match`, {
    method: "POST",
    body: JSON.stringify({
      groceryListId,
      ...(forceLineKeys && forceLineKeys.length > 0 && { forceLineKeys }),
    }),
  });
}

/**
 * Persist the user's chosen product/quantity/exclusion for one or more
 * grocery-list lines (#444 second half, closes #416) -- keyed by
 * `ingredientId`+`unit`, the same stable line identity the match cache
 * keys on. Fire-and-forget from MatchReview's perspective: a 400 means the
 * chosen product fell out of the line's current candidates (re-search
 * needed); a 409 means the persisted line itself is allergy-stale and must
 * be re-searched before a selection can be trusted at all.
 */
export async function updateRetailerSelections(
  retailerId: string,
  groceryListId: string,
  selections: RetailerLineSelection[],
): Promise<void> {
  await apiFetch<void>(`/retailers/${retailerId}/selections`, {
    method: "PATCH",
    body: JSON.stringify({ groceryListId, selections }),
  });
}

export interface KrogerCartSelection {
  upc: string;
  quantity: number;
  // Present only when the selection came from a real MatchReview match
  // (Track D, D1, issue #382) -- lets the handoff route persist the
  // confirmed product/price/aisle onto the exact GroceryItem row it was
  // matched against. Never reaches Kroger's cart API itself.
  groceryItemId?: string;
  productId?: string;
  price?: number;
  aisle?: string;
  // Rides along purely for the server's cart ledger "Added so far" label
  // (issue #441) -- Kroger's own cart-add call never receives it.
  description?: string;
}

export interface KrogerCartPushResult {
  /**
   * Lines THIS call's delta actually pushed to Kroger, per the
   * (groceryList, retailer, upc) ledger (#441) -- not a claim about what
   * Kroger's cart now contains (still write-only), but a fact about our own
   * recorded push history: a UPC already fully represented in the ledger
   * from an earlier push is NOT re-sent, and is counted in
   * `alreadyInCart` instead.
   */
  added: number;
  /** Lines this call found already fully recorded from an earlier push. */
  alreadyInCart: number;
  /** Total confirmed lines ever pushed for this list -- "Added so far". */
  cartCount: number;
  url: string;
}

/**
 * Push confirmed selections into the user's real Kroger cart, through the
 * server's idempotent cart ledger (#441): a reload or a second tab mid-push
 * can never duplicate an order, because the server diffs against what this
 * grocery list has already recorded as pushed and sends Kroger only the
 * delta. Checkout always happens on kroger.com (or the user's own banner's
 * storefront) via the returned url.
 *
 * `groceryListId` is required (Track D, D1, issue #382; also what the
 * ledger keys on): it scopes both the live-price capture that happens
 * server-side once the push succeeds, and the idempotency record itself.
 */
export async function pushKrogerCart(
  groceryListId: string,
  selections: KrogerCartSelection[],
): Promise<KrogerCartPushResult> {
  return apiFetch<KrogerCartPushResult>("/retailers/kroger/handoff", {
    method: "POST",
    body: JSON.stringify({ groceryListId, selections }),
  });
}

export interface KrogerCartStatus {
  /** Total confirmed lines ever pushed for this list -- "Added so far". */
  cartCount: number;
  lastPushedAt: string | null;
}

/**
 * Read-only "Added so far" count for a grocery list's Kroger cart ledger
 * (#441) -- lets the Groceries page show cumulative cart progress without
 * requiring a fresh push.
 */
export async function getKrogerCartStatus(
  groceryListId: string,
): Promise<KrogerCartStatus> {
  return apiFetch<KrogerCartStatus>(
    `/retailers/kroger/cart-status?groceryListId=${encodeURIComponent(groceryListId)}`,
  );
}

/**
 * The browser navigation target that starts the Kroger OAuth link. A full-page
 * redirect (not fetch) — the API 302s to Kroger's consent page. Same-origin
 * rewrite path per the repo's auth topology.
 */
export const KROGER_CONNECT_PATH = "/api/backend/retailers/kroger/connect";
